import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchWorkingHours, fetchAppointments, fetchFacilities, fetchStaff, saveWorkingHour } from "../../redux/thunks/calendarThunks";
import { setSelectedFacilities, setSelectedStaff } from "../../redux/slices/calendarSlice";
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { ContextMenu, MenuItem as ContextMenuItem, ContextMenuTrigger } from 'react-contextmenu';
// MUI imports
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Checkbox, 
  ListItemText, 
  OutlinedInput, 
  TextField, 
  Button, 
  Box, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Avatar
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PrintIcon from '@mui/icons-material/Print';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import { useUserData } from '../../hooks/useUserData';

// Styled Components - Compact Design
const CompactHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(1.5, 3),
  borderRadius: '12px',
  marginBottom: theme.spacing(1.5),
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const CompactButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '6px 12px',
  minWidth: 'auto',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
  },
}));

const CompactSelect = styled(FormControl)(({ theme }) => ({
  minWidth: 140,
  '& .MuiInputBase-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '8px',
    fontSize: '0.875rem',
    height: '36px',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.875rem',
  },
  '& .MuiSelect-icon': {
    color: 'rgba(255, 255, 255, 0.8)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  '& .MuiInputBase-input': {
    color: 'white',
    padding: '8px 12px',
  },
  // Fix dropdown menu item text colors
  '& .MuiMenuItem-root': {
    color: '#333',
  },
  '& .MuiListItemText-primary': {
    color: '#333',
  },
}));

const CalendarContainer = styled(Box)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
  backgroundColor: 'white',
  '& .rbc-calendar': {
    borderRadius: '12px',
    fontFamily: theme.typography.fontFamily,
  },
  '& .rbc-header': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontWeight: 600,
    padding: '8px 6px',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  '& .rbc-toolbar': {
    backgroundColor: theme.palette.grey[50],
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .rbc-toolbar-label': {
      fontSize: '1.1rem',
      fontWeight: 600,
    },
  },
  '& .rbc-toolbar button': {
    borderRadius: '6px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '4px 8px',
    margin: '0 2px',
    '&.rbc-active': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
    },
  },
  '& .rbc-event': {
    borderRadius: '4px',
    fontWeight: 500,
    fontSize: '0.8rem',
    padding: '2px 4px',
  },
  '& .rbc-today': {
    backgroundColor: theme.palette.primary.light + '20',
  },
  '& .rbc-time-slot': {
    fontSize: '0.8rem',
  },
  '& .rbc-time-header-content': {
    fontSize: '0.875rem',
  },
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-deleteIcon': {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '1rem',
    '&:hover': {
      color: 'white',
    },
  },
}));

const SideLegendPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e0e0e0',
  height: 'fit-content',
  minWidth: '200px',
  maxWidth: '250px',
}));

const StaffLegendItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected'
})(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  borderRadius: '8px',
  backgroundColor: selected ? '#e3f2fd' : 'white',
  border: selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
  marginBottom: theme.spacing(0.75),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: selected ? '#bbdefb' : '#f5f5f5',
    transform: 'translateX(2px)',
  },
  '&:last-child': {
    marginBottom: 0,
  },
}));

const locales = {
  'en-US': enUS,
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

export default function CalendarPage() {
  const dispatch = useDispatch();
  const { facilities, staff, workingHours, appointments, selectedFacilities, selectedStaff } = useSelector(state => state.calendar);
  const [whModalOpen, setWhModalOpen] = useState(false);
  const [whEditData, setWhEditData] = useState(null);
  const { user: currentUser } = useUserData();
  const [contextMenuSlotInfo, setContextMenuSlotInfo] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [legendFilterStaffId, setLegendFilterStaffId] = useState(null);

  useEffect(() => {
    dispatch(fetchFacilities());
    dispatch(fetchStaff());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser?.defaultFacility && facilities.length > 0 && staff.length > 0 && selectedFacilities.length === 0) {
      // defaultFacility is now the complete facility object, extract the ID
      const defaultFacilityId = typeof currentUser.defaultFacility === 'object' ? currentUser.defaultFacility.id : currentUser.defaultFacility;
      dispatch(setSelectedFacilities([defaultFacilityId]));
      
      const facilityStaff = staff.filter(s => Array.isArray(s.facilities) && s.facilities.includes(defaultFacilityId));
      const facilityStaffIds = facilityStaff.map(s => s.id);
      dispatch(setSelectedStaff(facilityStaffIds));
    }
  }, [currentUser, facilities, staff, dispatch, selectedFacilities.length]);

  useEffect(() => {
    if (selectedFacilities.length && selectedStaff.length) {
      dispatch(fetchWorkingHours(selectedFacilities, selectedStaff));
      dispatch(fetchAppointments(selectedFacilities, selectedStaff));
    }
  }, [dispatch, selectedFacilities, selectedStaff]);

  // Map working hours and appointments to calendar events
  const events = useMemo(() => {
    // Filter by legend selection if active
    const filteredWorkingHours = legendFilterStaffId 
      ? workingHours.filter(wh => wh.userId === legendFilterStaffId)
      : workingHours;
    const filteredAppointments = legendFilterStaffId
      ? appointments.filter(appt => appt.doctorId === legendFilterStaffId)
      : appointments;

    return [
      ...filteredWorkingHours.map(wh => ({
        id: `wh-${wh.id}`,
        title: `Working: ${staff.find(s => s.id === wh.userId)?.displayName || ''}`,
        start: new Date(wh.startTime),
        end: new Date(wh.endTime),
        allDay: false,
        resource: { type: 'workingHour', ...wh }
      })),
      ...filteredAppointments.map(appt => ({
        id: `appt-${appt.id}`,
        title: `Appt: ${appt.patientName || appt.id}`,
        start: new Date(appt.startTime),
        end: new Date(appt.endTime),
        allDay: false,
        resource: { type: 'appointment', ...appt }
      }))
    ];
  }, [workingHours, appointments, staff, legendFilterStaffId]);

  // Right-click handler for calendar slots
  const handleSelectSlot = slotInfo => {
    // Check for overlap before allowing scheduling
    if (isOverlappingStaffSchedule(slotInfo.start, slotInfo.end)) {
      alert('This time slot overlaps with existing staff schedule');
      return;
    }
    
    setWhEditData({
      dayOfWeek: slotInfo.start.getDay(),
      startTime: slotInfo.start.toISOString(),
      endTime: slotInfo.end.toISOString(),
    });
    setWhModalOpen(true);
  };

  // Handler for right-click on slot
  // const handleSlotRightClick = (slotInfo) => {
  //   setContextMenuSlotInfo(slotInfo);
  // };

  // Handler for context menu actions
  const handleContextMenuAction = (action, data) => {
    const slotInfo = data && data.slotInfo ? data.slotInfo : contextMenuSlotInfo;
    let fallbackSlot = {
      start: new Date(),
      end: new Date(new Date().getTime() + 30 * 60 * 1000), // 30 min slot
    };
    const useSlot = slotInfo || fallbackSlot;
    if (action === 'schedule') {
      setWhEditData({
        dayOfWeek: useSlot.start.getDay(),
        startTime: useSlot.start.toISOString(),
        endTime: useSlot.end.toISOString(),
      });
      setWhModalOpen(true);
    }
    setContextMenuSlotInfo(null);
  };

  // Custom slot wrapper for react-contextmenu
  function ContextMenuSlotWrapper(props) {
    const { children, value, resource } = props;
    return (
      <ContextMenuTrigger
        id="calendar-context-menu"
        collect={() => ({ slotInfo: { start: value, end: value, resource } })}
        holdToDisplay={-1}
        attributes={{ style: { height: '100%' } }}
      >
        {children}
      </ContextMenuTrigger>
    );
  }

  // Generate consistent color for each user
  const getUserColor = (userId) => {
    const colors = [
      '#1976d2', // Blue
      '#388e3c', // Green
      '#f57c00', // Orange
      '#7b1fa2', // Purple
      '#c2185b', // Pink
      '#00796b', // Teal
      '#5d4037', // Brown
      '#455a64', // Blue Grey
      '#e91e63', // Hot Pink
      '#ff5722', // Deep Orange
      '#9c27b0', // Purple
      '#2196f3', // Light Blue
      '#4caf50', // Light Green
      '#ff9800', // Amber
      '#795548', // Brown
      '#607d8b', // Blue Gray
      '#3f51b5', // Indigo
      '#009688', // Teal
      '#8bc34a', // Light Green
      '#ffc107', // Yellow
    ];
    
    // Generate a consistent index based on userId
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Custom event style getter for coloring
  const eventStyleGetter = event => {
    if (event.resource.type === 'workingHour') {
      const userColor = getUserColor(event.resource.userId);
      return { 
        style: { 
          backgroundColor: userColor, 
          color: '#fff', 
          borderRadius: 4,
          border: `2px solid ${userColor}`,
          opacity: 0.9
        } 
      };
    }
    if (event.resource.type === 'appointment') {
      const userColor = event.resource.doctorId ? getUserColor(event.resource.doctorId) : '#43a047';
      return { 
        style: { 
          backgroundColor: userColor, 
          color: '#fff', 
          borderRadius: 4,
          border: `2px solid ${userColor}`,
          opacity: 0.95,
          borderStyle: 'dashed' // Different border style for appointments
        } 
      };
    }
    return {};
  };

  // Prevent overlap: check if the new appointment overlaps with any staff schedule
  const isOverlappingStaffSchedule = (start, end) => {
    return workingHours.some(wh => {
      const whStart = new Date(wh.startTime).getTime();
      const whEnd = new Date(wh.endTime).getTime();
      return (new Date(start).getTime() < whEnd && new Date(end).getTime() > whStart);
    });
  };

  const selectedFacilityNames = facilities.filter(f => selectedFacilities.includes(f.id)).map(f => f.name);
  const selectedStaffNames = staff.filter(s => selectedStaff.includes(s.id)).map(s => s.displayName || s.name);

  // Listen for facility changes and refresh calendar data
  const handleFacilityChange = (newFacilityId, previousFacilityId) => {
    console.log('Calendar: Facility changed, refreshing calendar data');
    // Clear current selections and refresh data
    dispatch(setSelectedFacilities([]));
    dispatch(setSelectedStaff([]));
    dispatch(fetchFacilities());
    dispatch(fetchStaff());
  };

  useFacilityChangeListener(handleFacilityChange);

  return (
    <Box sx={{ maxWidth: '1400px', margin: '0 auto', p: { xs: 1, sm: 2 } }}>
      {/* Compact Header with Inline Controls */}
      <CompactHeader>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' } }}>
          {/* Title and Quick Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <CalendarTodayIcon sx={{ fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', flexShrink: 0 }}>
              Calendar & Scheduling
            </Typography>
            
            {/* Quick Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Tooltip title="Add Working Hours">
                <CompactButton
                  startIcon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setWhModalOpen(true)}
                  size="small"
                >
                  Hours
                </CompactButton>
              </Tooltip>
              <Tooltip title="Print My Shifts">
                <CompactButton
                  startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setPrintDialogOpen(true)}
                  size="small"
                >
                  Print Shifts
                </CompactButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Inline Filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Filter Toggle */}
            <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                sx={{ color: 'white', p: 0.5 }}
                size="small"
              >
                <FilterListIcon sx={{ fontSize: 20 }} />
                {showFilters ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>

            {/* Quick Filter Chips */}
            {!showFilters && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {selectedFacilityNames.length > 0 && (
                  <FilterChip
                    label={`${selectedFacilityNames.length} Facility${selectedFacilityNames.length > 1 ? 's' : ''}`}
                    size="small"
                  />
                )}
                {selectedStaffNames.length > 0 && (
                  <FilterChip
                    label={`${selectedStaffNames.length} Staff`}
                    size="small"
                  />
                )}
              </Box>
            )}

            {/* Expanded Filters */}
            {showFilters && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <CompactSelect size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Facilities</InputLabel>
                  <Select
                    multiple
                    value={selectedFacilities}
                    onChange={e => dispatch(setSelectedFacilities(e.target.value))}
                    input={<OutlinedInput label="Facilities" />}
                    renderValue={selected => 
                      selected.length === 0 ? 'All Facilities' :
                      selected.length === 1 ? facilities.find(f => f.id === selected[0])?.name :
                      `${selected.length} facilities`
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': { 
                            color: '#333 !important',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(25, 118, 210, 0.12)',
                              color: '#333 !important',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.20)',
                            }
                          },
                          '& .MuiListItemText-primary': { color: '#333 !important' },
                          '& .MuiTypography-root': { color: '#333 !important' }
                        }
                      }
                    }}
                  >
                    {facilities.map(facility => (
                      <MenuItem key={facility.id} value={facility.id}>
                        <Checkbox checked={selectedFacilities.includes(facility.id)} />
                        <ListItemText 
                          primary={facility.name} 
                          primaryTypographyProps={{ sx: { color: '#333 !important' } }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </CompactSelect>

                <CompactSelect size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Staff</InputLabel>
                  <Select
                    multiple
                    value={selectedStaff}
                    onChange={e => dispatch(setSelectedStaff(e.target.value))}
                    input={<OutlinedInput label="Staff" />}
                    renderValue={selected => 
                      selected.length === 0 ? 'All Staff' :
                      selected.length === 1 ? staff.find(s => s.id === selected[0])?.displayName :
                      `${selected.length} staff`
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': { 
                            color: '#333 !important',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(25, 118, 210, 0.12)',
                              color: '#333 !important',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.20)',
                            }
                          },
                          '& .MuiListItemText-primary': { color: '#333 !important' },
                          '& .MuiTypography-root': { color: '#333 !important' }
                        }
                      }
                    }}
                  >
                    {staff.map(user => (
                      <MenuItem key={user.id} value={user.id}>
                        <Checkbox checked={selectedStaff.includes(user.id)} />
                        <ListItemText 
                          primary={user.displayName || user.name} 
                          primaryTypographyProps={{ sx: { color: '#333 !important' } }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </CompactSelect>
              </Box>
            )}
          </Box>
        </Box>
      </CompactHeader>

      {/* Calendar with Side Legend Layout */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Main Calendar */}
        <CalendarContainer sx={{ flex: 1 }}>
          <ContextMenuTrigger id="calendar-context-menu" holdToDisplay={-1} attributes={{ style: { width: '100%' } }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: window.innerHeight * 0.75 }}
              selectable
              onSelectSlot={handleSelectSlot}
              onDoubleClickEvent={handleSelectSlot}
              eventPropGetter={eventStyleGetter}
              components={{
                timeSlotWrapper: (slotProps) => (
                  <ContextMenuSlotWrapper {...slotProps} />
                ),
              }}
              views={['month', 'week', 'day']}
              defaultView="week"
              popup={true}
              step={30}
              timeslots={2}
            />
          </ContextMenuTrigger>
        </CalendarContainer>

        {/* Side Legend Panel */}
        {selectedStaff.length > 0 && (
          <SideLegendPanel elevation={0}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 1.5
            }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 700, 
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <PersonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                Staff Legend
              </Typography>
              {legendFilterStaffId && (
                <Tooltip title="Clear Filter">
                  <IconButton 
                    size="small" 
                    onClick={() => setLegendFilterStaffId(null)}
                    sx={{ 
                      p: 0.5,
                      backgroundColor: '#ffebee',
                      '&:hover': { backgroundColor: '#ffcdd2' }
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {legendFilterStaffId && (
              <Typography variant="caption" sx={{ 
                display: 'block', 
                mb: 1, 
                color: '#1976d2',
                fontWeight: 500,
                fontSize: '0.7rem'
              }}>
                Filtered to: {staff.find(s => s.id === legendFilterStaffId)?.displayName || 'Selected Staff'}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {staff
                .filter(user => selectedStaff.includes(user.id))
                .map(user => (
                  <StaffLegendItem 
                    key={user.id} 
                    selected={legendFilterStaffId === user.id}
                    onClick={() => setLegendFilterStaffId(
                      legendFilterStaffId === user.id ? null : user.id
                    )}
                  >
                    <Avatar
                      sx={{ 
                        width: 28, 
                        height: 28, 
                        backgroundColor: getUserColor(user.id),
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                    >
                      {(user.displayName || user.name || user.email || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.8rem', 
                          fontWeight: legendFilterStaffId === user.id ? 600 : 500,
                          color: '#333',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {user.displayName || user.name || user.email}
                      </Typography>
                      {(user.role || user.roles) && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.65rem', 
                            color: '#888',
                            textTransform: 'capitalize'
                          }}
                        >
                          {(() => {
                            const roles = user.roles || (Array.isArray(user.role) ? user.role : [user.role]);
                            return roles
                              .filter(Boolean)
                              .map(r => r.replace(/_/g, ' '))
                              .join(', ');
                          })()}
                        </Typography>
                      )}
                    </Box>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '3px',
                        backgroundColor: getUserColor(user.id),
                        flexShrink: 0
                      }} 
                    />
                  </StaffLegendItem>
                ))
              }
            </Box>
            
            {/* Legend Info */}
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                <Box component="span" sx={{ 
                  display: 'inline-block', 
                  width: 10, 
                  height: 10, 
                  borderRadius: '2px',
                  backgroundColor: '#1976d2',
                  mr: 0.5,
                  verticalAlign: 'middle'
                }} />
                Solid border = Working Hours
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>
                <Box component="span" sx={{ 
                  display: 'inline-block', 
                  width: 10, 
                  height: 10, 
                  borderRadius: '2px',
                  backgroundColor: '#43a047',
                  border: '2px dashed #43a047',
                  mr: 0.5,
                  verticalAlign: 'middle'
                }} />
                Dashed border = Appointments
              </Typography>
            </Box>
          </SideLegendPanel>
        )}
      </Box>

      <ContextMenu id="calendar-context-menu">
        <ContextMenuItem onClick={(e, data) => handleContextMenuAction('schedule', data)}>
          Add Your Schedule
        </ContextMenuItem>
      </ContextMenu>

      <CompactWorkingHoursForm
        open={whModalOpen}
        onClose={() => setWhModalOpen(false)}
        onSave={workingHoursArray => {
          // Handle multiple working hours
          workingHoursArray.forEach(workingHour => {
            const workingHourWithFacility = {
              ...workingHour,
              facilityId: selectedFacilities[0]
            };
            dispatch(saveWorkingHour(workingHourWithFacility, selectedFacilities, selectedStaff));
          });
          setWhModalOpen(false);
        }}
        initialData={whEditData}
        facilityStaff={staff.filter(s => selectedFacilities.some(fId => Array.isArray(s.facilities) && s.facilities.includes(fId)))}
        selectedFacilityId={selectedFacilities[0]}
      />

      <PrintShiftsDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        workingHours={workingHours}
        currentUser={currentUser}
      />
    </Box>
  );
}

function CompactWorkingHoursForm({ open, onClose, onSave, initialData, facilityStaff = [], selectedFacilityId }) {
  const { user: currentUser, isAdmin, isFacilityAdmin } = useUserData();
  const { workingHours } = useSelector(state => state.calendar);
  
  // Selected staff member for assigning hours
  const [selectedStaffId, setSelectedStaffId] = React.useState('');
  const canAssignToOthers = isAdmin || isFacilityAdmin;
  
  // Generate next 30 days
  const getNext30Days = () => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push({
        date: date,
        dateString: date.toISOString().split('T')[0],
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: date.getDate(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      });
    }
    return days;
  };

  const [next30Days] = React.useState(getNext30Days());
  const [scheduleData, setScheduleData] = React.useState({});
  
  // Get the effective user ID (either selected staff or current user)
  const effectiveUserId = canAssignToOthers && selectedStaffId ? selectedStaffId : currentUser?.id;
  const effectiveUserName = canAssignToOthers && selectedStaffId 
    ? facilityStaff.find(s => s.id === selectedStaffId)?.displayName || facilityStaff.find(s => s.id === selectedStaffId)?.name
    : currentUser?.displayName || currentUser?.name || 'Yourself';

  // Reset selected staff when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedStaffId('');
    }
  }, [open]);

  React.useEffect(() => {
    if (open && effectiveUserId) {
      // Initialize with existing working hours or default times
      const initialSchedule = {};
      next30Days.forEach(day => {
        const existingHour = workingHours.find(wh => 
          wh.userId === effectiveUserId && 
          new Date(wh.startTime).toDateString() === day.date.toDateString()
        );
        
        if (existingHour) {
          initialSchedule[day.dateString] = {
            startTime: new Date(existingHour.startTime).toLocaleTimeString('en-US', { 
              hour12: false, hour: '2-digit', minute: '2-digit' 
            }),
            endTime: new Date(existingHour.endTime).toLocaleTimeString('en-US', { 
              hour12: false, hour: '2-digit', minute: '2-digit' 
            }),
            isWorking: true
          };
        } else {
          initialSchedule[day.dateString] = {
            startTime: day.isWeekend ? '' : '08:00',
            endTime: day.isWeekend ? '' : '17:00',
            isWorking: !day.isWeekend
          };
        }
      });
      setScheduleData(initialSchedule);
    }
  }, [open, workingHours, effectiveUserId, next30Days]);

  const copyFromLastWeek = () => {
    const newSchedule = { ...scheduleData };
    next30Days.forEach(day => {
      const lastWeekDate = new Date(day.date);
      lastWeekDate.setDate(lastWeekDate.getDate() - 7);
      //const lastWeekString = lastWeekDate.toISOString().split('T')[0];
      
      const lastWeekHour = workingHours.find(wh => 
        wh.userId === effectiveUserId && 
        new Date(wh.startTime).toDateString() === lastWeekDate.toDateString()
      );
      
      if (lastWeekHour) {
        newSchedule[day.dateString] = {
          startTime: new Date(lastWeekHour.startTime).toLocaleTimeString('en-US', { 
            hour12: false, hour: '2-digit', minute: '2-digit' 
          }),
          endTime: new Date(lastWeekHour.endTime).toLocaleTimeString('en-US', { 
            hour12: false, hour: '2-digit', minute: '2-digit' 
          }),
          isWorking: true
        };
      }
    });
    setScheduleData(newSchedule);
  };

  const copyFromLast4Weeks = () => {
    const newSchedule = { ...scheduleData };
    const last4WeeksHours = workingHours.filter(wh => {
      const whDate = new Date(wh.startTime);
      const fourWeeksAgo = new Date();
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
      return wh.userId === effectiveUserId && whDate >= fourWeeksAgo;
    });

    // Group by day of week
    const patternByDayOfWeek = {};
    last4WeeksHours.forEach(wh => {
      const dayOfWeek = new Date(wh.startTime).getDay();
      if (!patternByDayOfWeek[dayOfWeek]) {
        patternByDayOfWeek[dayOfWeek] = [];
      }
      patternByDayOfWeek[dayOfWeek].push({
        startTime: new Date(wh.startTime).toLocaleTimeString('en-US', { 
          hour12: false, hour: '2-digit', minute: '2-digit' 
        }),
        endTime: new Date(wh.endTime).toLocaleTimeString('en-US', { 
          hour12: false, hour: '2-digit', minute: '2-digit' 
        })
      });
    });

    // Apply most common pattern for each day of week
    next30Days.forEach(day => {
      const dayOfWeek = day.date.getDay();
      const patterns = patternByDayOfWeek[dayOfWeek];
      if (patterns && patterns.length > 0) {
        // Use the most recent pattern
        const mostRecent = patterns[patterns.length - 1];
        newSchedule[day.dateString] = {
          ...mostRecent,
          isWorking: true
        };
      }
    });
    setScheduleData(newSchedule);
  };

  const updateDaySchedule = (dateString, field, value) => {
    setScheduleData(prev => ({
      ...prev,
      [dateString]: {
        ...prev[dateString],
        [field]: value
      }
    }));
  };

  const toggleWorkingDay = (dateString) => {
    setScheduleData(prev => ({
      ...prev,
      [dateString]: {
        ...prev[dateString],
        isWorking: !prev[dateString]?.isWorking,
        startTime: !prev[dateString]?.isWorking ? '08:00' : '',
        endTime: !prev[dateString]?.isWorking ? '17:00' : ''
      }
    }));
  };

  const handleSaveAll = () => {
    const workingHoursToSave = [];
    Object.entries(scheduleData).forEach(([dateString, schedule]) => {
      if (schedule.isWorking && schedule.startTime && schedule.endTime) {
        workingHoursToSave.push({
          userId: effectiveUserId,
          startTime: `${dateString}T${schedule.startTime}:00.000Z`,
          endTime: `${dateString}T${schedule.endTime}:00.000Z`,
          dayOfWeek: new Date(dateString).getDay()
        });
      }
    });
    onSave(workingHoursToSave);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', 
        color: 'white',
        fontWeight: 600,
        py: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              Working Hours - Next 30 Days
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <CompactButton 
              size="small" 
              onClick={copyFromLastWeek}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
            >
              Copy Last Week
            </CompactButton>
            <CompactButton 
              size="small" 
              onClick={copyFromLast4Weeks}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
            >
              Copy Last 4 Weeks
            </CompactButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2, maxHeight: '70vh', overflow: 'auto' }}>
        {/* Staff Selection for Admins */}
        {canAssignToOthers && facilityStaff.length > 0 && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: '#e3f2fd', 
            borderRadius: 2,
            border: '1px solid #90caf9'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 18 }} />
              Assign Hours To
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedStaffId}
                onChange={(e) => setSelectedStaffId(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: 'white',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                      {(currentUser?.displayName || currentUser?.name || 'Y').charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2">
                      Yourself ({currentUser?.displayName || currentUser?.name || currentUser?.email})
                    </Typography>
                  </Box>
                </MenuItem>
                {facilityStaff
                  .filter(s => s.id !== currentUser?.id)
                  .map(staffMember => (
                    <MenuItem key={staffMember.id} value={staffMember.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'secondary.main' }}>
                          {(staffMember.displayName || staffMember.name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {staffMember.displayName || staffMember.name || staffMember.email}
                          </Typography>
                          {(staffMember.role || staffMember.roles) && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                              {(() => {
                                const roles = staffMember.roles || (Array.isArray(staffMember.role) ? staffMember.role : [staffMember.role]);
                                return roles
                                  .filter(Boolean)
                                  .map(r => r.replace(/_/g, ' '))
                                  .join(', ');
                              })()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            {selectedStaffId && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#1565c0' }}>
                Setting schedule for: <strong>{effectiveUserName}</strong>
              </Typography>
            )}
          </Box>
        )}

        <Grid container spacing={1}>
          {/* Header Row */}
          <Grid item xs={2}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Date
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Day
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Working
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Start Time
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              End Time
            </Typography>
          </Grid>

          {/* Data Rows */}
          {next30Days.map((day) => {
            const daySchedule = scheduleData[day.dateString] || {};
            return (
              <React.Fragment key={day.dateString}>
                <Grid item xs={2}>
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.8rem',
                    color: day.isWeekend ? 'text.secondary' : 'text.primary'
                  }}>
                    {day.dayNumber}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.8rem',
                    fontWeight: day.isWeekend ? 400 : 500,
                    color: day.isWeekend ? 'text.secondary' : 'text.primary'
                  }}>
                    {day.dayName}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Checkbox
                    size="small"
                    checked={daySchedule.isWorking || false}
                    onChange={() => toggleWorkingDay(day.dateString)}
                    sx={{ p: 0.5 }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    type="time"
                    size="small"
                    value={daySchedule.startTime || ''}
                    onChange={(e) => updateDaySchedule(day.dateString, 'startTime', e.target.value)}
                    disabled={!daySchedule.isWorking}
                    InputProps={{
                      sx: { fontSize: '0.8rem', height: '32px' }
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    type="time"
                    size="small"
                    value={daySchedule.endTime || ''}
                    onChange={(e) => updateDaySchedule(day.dateString, 'endTime', e.target.value)}
                    disabled={!daySchedule.isWorking}
                    InputProps={{
                      sx: { fontSize: '0.8rem', height: '32px' }
                    }}
                    fullWidth
                  />
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
          {Object.values(scheduleData).filter(s => s.isWorking).length} working days for {effectiveUserName}
        </Typography>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: '8px' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSaveAll} sx={{ borderRadius: '8px' }}>
          Save {selectedStaffId ? `for ${effectiveUserName}` : ''} ({Object.values(scheduleData).filter(s => s.isWorking).length} days)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PrintShiftsDialog({ open, onClose, workingHours, currentUser }) {
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [filteredShifts, setFilteredShifts] = React.useState([]);
  const [userWorkingHours, setUserWorkingHours] = React.useState([]);

  // Use the working hours already loaded by the calendar instead of fetching separately
  React.useEffect(() => {
    if (!open || !currentUser?.uid) {
      setUserWorkingHours([]);
      return;
    }
    
    // Filter working hours to only include the current user's hours
    const userHours = workingHours.filter(wh => wh.userId === currentUser.uid);
    
    setUserWorkingHours(userHours);
  }, [open, currentUser?.uid, workingHours]);

  React.useEffect(() => {
    if (open && !startDate && !endDate) {
      // Set default date range to last 3 months to next 3 months
      const now = new Date();
      const startRange = new Date(now);
      startRange.setMonth(now.getMonth() - 3);
      startRange.setDate(1); // First day of that month
      
      const endRange = new Date(now);
      endRange.setMonth(now.getMonth() + 3);
      endRange.setDate(0); // Last day of that month
      
      setStartDate(startRange.toISOString().split('T')[0]);
      setEndDate(endRange.toISOString().split('T')[0]);
    }
  }, [open, startDate, endDate]);

  React.useEffect(() => {
    if (startDate && endDate && currentUser && userWorkingHours.length >= 0) {
      const userShifts = userWorkingHours.filter(wh => {
        // Parse the shift start time
        const shiftDate = new Date(wh.startTime);
        
        // Create proper date boundaries with explicit timezone handling
        const start = new Date(startDate + 'T00:00:00.000');
        const end = new Date(endDate + 'T23:59:59.999');
        
        return shiftDate >= start && shiftDate <= end;
      }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      
      setFilteredShifts(userShifts);
    }
  }, [startDate, endDate, currentUser, userWorkingHours]);

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>My Shifts Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #1976d2; padding-bottom: 10px; }
            .date-range { text-align: center; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #1976d2; color: white; font-weight: 600; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .total-row { background-color: #e3f2fd; font-weight: 600; }
            .no-shifts { text-align: center; color: #666; font-style: italic; padding: 40px; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>My Shifts Report</h1>
            <h3>${currentUser?.displayName || currentUser?.name || 'Staff Member'}</h3>
          </div>
          <div class="date-range">
            Period: ${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}
          </div>
          ${filteredShifts.length === 0 ? 
            '<div class="no-shifts">No shifts found for the selected date range.</div>' :
            `<table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Hours</th>
                </tr>
              </thead>
              <tbody>
                ${filteredShifts.map(shift => {
                  const start = new Date(shift.startTime);
                  const end = new Date(shift.endTime);
                  const hours = ((end - start) / (1000 * 60 * 60)).toFixed(1);
                  return `
                    <tr>
                      <td>${start.toLocaleDateString()}</td>
                      <td>${start.toLocaleDateString('en-US', { weekday: 'long' })}</td>
                      <td>${start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                      <td>${end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                      <td>${hours}h</td>
                    </tr>
                  `;
                }).join('')}
                <tr class="total-row">
                  <td colspan="4"><strong>Total Hours</strong></td>
                  <td><strong>${filteredShifts.reduce((total, shift) => {
                    const hours = (new Date(shift.endTime) - new Date(shift.startTime)) / (1000 * 60 * 60);
                    return total + hours;
                  }, 0).toFixed(1)}h</strong></td>
                </tr>
              </tbody>
            </table>`
          }
          <br><br>
          <div style="font-size: 12px; color: #666; text-align: center;">
            Generated on ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const totalHours = filteredShifts.reduce((total, shift) => {
    const hours = (new Date(shift.endTime) - new Date(shift.startTime)) / (1000 * 60 * 60);
    return total + hours;
  }, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', 
        color: 'white',
        fontWeight: 600,
        py: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Print My Shifts
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                Start Date
              </Typography>
              <DatePicker
                value={startDate ? new Date(startDate) : null}
                onChange={(date) => setStartDate(date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: { 
                    fullWidth: true, 
                    size: 'small',
                    sx: {
                      '& .MuiInputBase-input': {
                        fontSize: '0.875rem'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                End Date
              </Typography>
              <DatePicker
                value={endDate ? new Date(endDate) : null}
                onChange={(date) => setEndDate(date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: { 
                    fullWidth: true, 
                    size: 'small',
                    sx: {
                      '& .MuiInputBase-input': {
                        fontSize: '0.875rem'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Quick Date Range Buttons */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const now = new Date();
              const start = new Date(now.getFullYear(), now.getMonth(), 1);
              const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            This Month
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const now = new Date();
              const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              const end = new Date(now.getFullYear(), now.getMonth(), 0);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            Last Month
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const now = new Date();
              const start = new Date(now);
              start.setDate(start.getDate() - 30);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(now.toISOString().split('T')[0]);
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            Last 30 Days
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const now = new Date();
              const start = new Date(now.getFullYear(), 0, 1);
              const end = new Date(now.getFullYear(), 11, 31);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            All {new Date().getFullYear()}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              // Match the exact range from add-central-hospital-shift-hours.js script
              const now = new Date();
              const start = new Date(now);
              start.setMonth(now.getMonth() - 2);
              const end = new Date(now);
              end.setMonth(now.getMonth() + 2);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            Script Range (±2 months)
          </Button>
        </Box>



        {filteredShifts.length > 0 ? (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, color: 'primary.main', fontSize: '0.9rem', fontWeight: 600 }}>
              Preview - {filteredShifts.length} shift{filteredShifts.length !== 1 ? 's' : ''} found
            </Typography>
            <Box sx={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: 1, 
              overflow: 'hidden',
              maxHeight: '350px',
              overflowY: 'auto'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ backgroundColor: '#1976d2', color: 'white' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Date</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Day</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Start</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>End</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredShifts.map((shift, index) => {
                    const start = new Date(shift.startTime);
                    const end = new Date(shift.endTime);
                    const hours = ((end - start) / (1000 * 60 * 60)).toFixed(1);
                    return (
                      <tr key={shift.id || index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <td style={{ padding: '4px 8px', fontSize: '0.75rem' }}>
                          {start.toLocaleDateString()}
                        </td>
                        <td style={{ padding: '4px 8px', fontSize: '0.75rem' }}>
                          {start.toLocaleDateString('en-US', { weekday: 'short' })}
                        </td>
                        <td style={{ padding: '4px 8px', fontSize: '0.75rem' }}>
                          {start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                        </td>
                        <td style={{ padding: '4px 8px', fontSize: '0.75rem' }}>
                          {end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                        </td>
                        <td style={{ padding: '4px 8px', fontSize: '0.75rem', fontWeight: 500 }}>
                          {hours}h
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 600, position: 'sticky', bottom: 0 }}>
                    <td colSpan={4} style={{ padding: '6px 8px', fontSize: '0.8rem' }}>
                      <strong>Total Hours</strong>
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: '0.8rem' }}>
                      <strong>{totalHours.toFixed(1)}h</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
              No shifts found for the selected date range.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
          {filteredShifts.length > 0 ? `${totalHours.toFixed(1)} total hours` : 'Select date range to view shifts'}
        </Typography>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: '8px' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePrint}
          disabled={filteredShifts.length === 0}
          startIcon={<PrintIcon />}
          sx={{ borderRadius: '8px' }}
        >
          Print Report
        </Button>
      </DialogActions>
    </Dialog>
  );
} 